import React from "react";
import {
  HeartHandshake,
  Award,
  Compass,
  ShieldCheck,
  Code2,
  Boxes,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { GlowOrb, Overline } from "../components/SectionDecor";

const icons = [HeartHandshake, Award, Compass, ShieldCheck];

const About = () => {
  const { t } = useLanguage();
  return (
    <div data-testid="page-about" className="relative">
      <GlowOrb color="#00ffb3" size={420} opacity={0.1} className="top-10 -left-20" />

      <section className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-24 pb-16">
        <div className="max-w-3xl">
          <Overline>{t.about.overline}</Overline>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter">
            {t.about.title}
          </h1>
          <p className="mt-6 text-lg text-zinc-300 leading-relaxed">{t.about.mission}</p>
        </div>
      </section>

      <section className="relative mx-auto max-w-7xl px-6 lg:px-10 pb-16">
        <div className="smk-card rounded-2xl p-8 lg:p-12">
          <Overline>{t.about.expertiseTitle}</Overline>
          <div className="mt-6 flex flex-wrap gap-3" data-testid="expertise-tags">
            {t.about.expertise.map((e) => (
              <span
                key={e}
                className="px-4 py-2 rounded-full border border-white/10 text-sm text-zinc-200 hover:border-[#00d4ff]/50 hover:bg-white/5 transition"
              >
                {e}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* IaC + Orchestration */}
      <section className="relative mx-auto max-w-7xl px-6 lg:px-10 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="smk-card rounded-2xl p-8 lg:p-10" data-testid="iac-card">
          <div className="h-12 w-12 rounded-xl smk-gradient-bg flex items-center justify-center">
            <Code2 size={20} className="text-black" />
          </div>
          <p className="mt-5 smk-overline">DevOps · Automation</p>
          <h3 className="mt-2 text-2xl lg:text-3xl font-semibold tracking-tight">
            {t.about.iacTitle}
          </h3>
          <p className="mt-3 text-zinc-400 leading-relaxed">{t.about.iacDesc}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {t.about.iacTools.map((tool) => (
              <span
                key={tool}
                data-testid={`iac-tool-${tool.toLowerCase()}`}
                className="px-3 py-1.5 rounded-md font-mono text-xs bg-white/5 border border-white/10 text-zinc-200 hover:border-[#00d4ff]/50 hover:text-white transition"
              >
                {tool}
              </span>
            ))}
          </div>
        </div>

        <div className="smk-card rounded-2xl p-8 lg:p-10" data-testid="orchestration-card">
          <div className="h-12 w-12 rounded-xl smk-gradient-bg flex items-center justify-center">
            <Boxes size={20} className="text-black" />
          </div>
          <p className="mt-5 smk-overline">Containers · Scale</p>
          <h3 className="mt-2 text-2xl lg:text-3xl font-semibold tracking-tight">
            {t.about.orchestrationTitle}
          </h3>
          <p className="mt-3 text-zinc-400 leading-relaxed">{t.about.orchestrationDesc}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {t.about.orchestrationTools.map((tool) => (
              <span
                key={tool}
                data-testid={`orch-tool-${tool.toLowerCase().replace(/\s/g, '-')}`}
                className="px-3 py-1.5 rounded-md font-mono text-xs bg-white/5 border border-white/10 text-zinc-200 hover:border-[#00d4ff]/50 hover:text-white transition"
              >
                {tool}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="relative mx-auto max-w-7xl px-6 lg:px-10 pb-28">
        <Overline>{t.about.valuesTitle}</Overline>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.about.values.map((v, i) => {
            const Icon = icons[i % icons.length];
            return (
              <div key={v.title} data-testid={`value-card-${i}`} className="smk-card rounded-2xl p-7">
                <div className="h-11 w-11 rounded-xl smk-gradient-bg flex items-center justify-center">
                  <Icon size={18} className="text-black" />
                </div>
                <h3 className="mt-5 text-lg font-semibold">{v.title}</h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{v.desc}</p>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default About;
